// src/Services/purchaseTracker.js
// Tells the buyer what happened after they pay.
//
// Bachs (and Paddle) purchases are granted by our webhook a moment after the
// buyer pays, so the browser doesn't get a receipt. This remembers the purchase,
// watches for it to land (coins, seats or Pro showing up on the account) and
// shows the result in the status banner (Components/PurchaseStatusBanner).
//
// The banner only says "adding your purchase" when the buyer has actually come
// back from a successful checkout (?payment=success). Someone who left the
// checkout page without paying is never told a payment is on its way; their
// pending purchase is just checked quietly on later loads until it expires.
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import authService from "./authService";
import useCoinStore from "../stores/coinStore";
import usePurchaseStatusStore from "../stores/purchaseStatusStore";
import { PRODUCTS } from "./products";
import { SELLER } from "../content/legal";

const PENDING_KEY = "nameit_pending_purchase";
const POLL_MS = 2000;
const WAIT_MS = 60 * 1000; // how long we watch before saying it's taking a while
const MAX_AGE_MS = 30 * 60 * 1000; // stop remembering a purchase after this
const DAY_MS = 24 * 60 * 60 * 1000;

let running = false;

function readPending() {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
  } catch {
    return null;
  }
}

function writePending(pending) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Storage can be blocked; tracking still works until the page is closed.
  }
}

function clearPending() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// "1,000 coins", "2 extra seats", "NameIt! Pro": what the buyer is waiting for.
function purchaseLabel(productId) {
  const product = PRODUCTS[productId] || {};
  if (product.coins) return `${product.coins.toLocaleString()} coins`;
  if (product.seats) return `${product.seats} extra seat${product.seats === 1 ? "" : "s"}`;
  if (product.proType === "onetime") return "Your NameIt! Pro pass";
  return "NameIt! Pro";
}

// What to tell the buyer once the purchase is on their account.
function successCopy(productId, { roomId } = {}) {
  const product = PRODUCTS[productId] || {};
  if (product.coins) return { title: `${purchaseLabel(productId)} added to your balance`, message: "" };
  if (product.seats) {
    return {
      title: `${purchaseLabel(productId)} added`,
      message: roomId ? "They're on your room now." : "They'll apply to your next room.",
    };
  }
  return { title: `${purchaseLabel(productId)} is active`, message: "" };
}

/**
 * Money was taken but we couldn't confirm the purchase: say so at the top of
 * the screen, with the reference and a one-tap way to reach us.
 */
export function showPurchaseProblem({ title = "We couldn't confirm your payment", message, reference = null }) {
  usePurchaseStatusStore.getState().show({
    status: "failed",
    title,
    message: message || "You don't need to pay again. Email us and we'll add it to your account.",
    reference,
    contactEmail: SELLER.email,
  });
}

export function showPurchaseSuccess(productId, options) {
  usePurchaseStatusStore.getState().show({ status: "success", reference: null, ...successCopy(productId, options) });
}

async function isGranted(pending) {
  const product = PRODUCTS[pending.productId];
  if (!product) return false;

  if (product.seats) {
    if (pending.roomId) {
      const room = await getDoc(doc(db, "rooms", pending.roomId));
      if (room.exists() && (room.data().maxPlayers || 0) >= pending.baseline.maxPlayers + product.seats) return true;
    }
    // Not the room's host: seats become a credit for the buyer's next room, which
    // expires 30 days after it is created, so a fresh one dates from this purchase.
    const uid = authService.getCurrentUserIdIfExists();
    if (uid) {
      const credit = (await getDoc(doc(db, "users", uid))).data()?.roomUpgradeCredit;
      const expiresAt = credit?.expiresAt?.toDate?.();
      if (expiresAt && expiresAt.getTime() >= pending.startedAt + 30 * DAY_MS - 60 * 1000) return true;
    }
    return false;
  }

  await useCoinStore.getState().syncBalance();
  const state = useCoinStore.getState();
  if (product.coins) return state.coinBalance >= pending.baseline.coins + product.coins;
  if (product.proType) {
    if (!state.isPro) return false;
    if (!pending.baseline.isPro) return true;
    return !!state.proExpiry && new Date(state.proExpiry) > new Date(pending.baseline.proExpiry || 0);
  }
  return false;
}

/**
 * Watches for the purchase to land. With `announce`, the buyer just came back
 * from paying: show "adding…" now and say so if it takes a while. Without it,
 * check quietly and only speak up once it has actually arrived.
 */
async function watch(pending, { announce }) {
  if (running) return;
  running = true;
  const status = usePurchaseStatusStore.getState();
  try {
    if (announce && !pending.notified) {
      status.show({
        status: "processing",
        title: `Adding ${purchaseLabel(pending.productId)}…`,
        message: "Usually a few seconds. Keep playing; we'll tell you when it lands.",
        reference: pending.transactionId,
      });
    }

    // Always check at least once, so a purchase that landed while the page was
    // closed is confirmed straight away on the next load. The wait counts from
    // now, not from checkout: time spent on the payment page doesn't count.
    const watchingSince = Date.now();
    for (;;) {
      try {
        if (await isGranted(pending)) {
          clearPending();
          showPurchaseSuccess(pending.productId, pending);
          return;
        }
      } catch (error) {
        console.warn("Checking purchase status failed, will retry:", error);
      }
      if (Date.now() - watchingSince >= WAIT_MS) break;
      await sleep(POLL_MS);
    }

    // Still not there. Say so once, only to someone we told it was coming; a
    // later page load re-checks quietly.
    if (announce && !pending.notified) {
      writePending({ ...pending, notified: true });
      usePurchaseStatusStore.getState().show({
        status: "delayed",
        title: "Your purchase is taking longer than usual",
        message: "You don't need to pay again. If it isn't here in a few minutes, email us and we'll sort it out.",
        reference: pending.transactionId,
        contactEmail: SELLER.email,
      });
    }
  } finally {
    running = false;
  }
}

/** Call right after a Paddle checkout completes. `baseline` is the account before paying. */
export function trackPurchase({ productId, roomId = null, transactionId = null, baseline }) {
  const pending = { productId, roomId, transactionId, baseline, startedAt: Date.now(), notified: false };
  writePending(pending);
  return watch(pending, { announce: true });
}

/**
 * Call just before sending the buyer to a hosted checkout page (Bachs). Nothing
 * is shown yet: the next app load picks it up via resumePendingPurchase, unless
 * the buyer came back with ?payment=cancelled.
 */
export function rememberPendingPurchase({ productId, roomId = null, transactionId = null, baseline }) {
  writePending({ productId, roomId, transactionId, baseline, startedAt: Date.now(), notified: false });
}

// Reads and removes ?payment=success|cancelled (set by the checkout's return
// URLs), so a refresh or a shared link doesn't replay it.
function takeCheckoutOutcome() {
  const url = new URL(window.location.href);
  const outcome = url.searchParams.get("payment");
  if (!outcome) return null;
  url.searchParams.delete("payment");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  return outcome;
}

/** Call once when the app loads: finishes off a purchase interrupted by a redirect or refresh. */
export async function resumePendingPurchase() {
  const outcome = takeCheckoutOutcome();
  if (outcome === "cancelled") {
    clearPending();
    return;
  }
  const pending = readPending();
  if (!pending) return;
  if (Date.now() - pending.startedAt > MAX_AGE_MS) {
    clearPending();
    return;
  }
  try {
    await authService.waitForInit();
  } catch {
    // Carry on: isGranted copes with a missing user.
  }
  return watch(pending, { announce: outcome === "success" });
}
